// typescript/lib/seavien-commons/src/content-import/concierge/markdown.ts
//
// A small, deterministic Markdown -> HTML step for the concierge adapters. The workspace carries no
// Markdown library and the converter only needs block structure + inline emphasis/links to build a faithful
// draft; anything this does not recognise stays as paragraph text, which the verbatim guard preserves.
// Covered: ATX headings, paragraphs, blank-line separation, `-`/`*`/`+` and `1.` lists (one level), fenced
// code, `> ` quotes, `**bold**`, `*em*`/`_em_`, `` `code` ``, `[text](url)`, `![alt](src)`, `---` rules.

/** The one HTML text escaper the concierge adapters share (commons review C-F27: there were three, one incomplete). */
export function escapeHtml(text: string): string {
    return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function inline(text: string): string {
    let out = escapeHtml(text);
    out = out.replace(/`([^`]+)`/g, (_m, code: string) => `<code>${code}</code>`);
    out = out.replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, (_m, alt: string, src: string) => `<img alt="${alt}" src="${src}">`);
    out = out.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_m, label: string, href: string) => `<a href="${href}">${label}</a>`);
    out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    out = out.replace(/(^|[^*\w])\*([^*\n]+)\*(?!\w)/g, "$1<em>$2</em>");
    out = out.replace(/(^|[^_\w])_([^_\n]+)_(?!\w)/g, "$1<em>$2</em>");
    return out;
}

export function markdownToHtml(markdown: string): string {
    const lines = markdown.replace(/\r\n?/g, "\n").split("\n");
    const html: string[] = [];
    let paragraph: string[] = [];
    let list: { tag: "ul" | "ol"; items: string[] } | null = null;
    let quote: string[] = [];
    let fence: string[] | null = null;

    const flushParagraph = () => {
        if (paragraph.length) {
            html.push(`<p>${inline(paragraph.join(" "))}</p>`);
            paragraph = [];
        }
    };
    const flushList = () => {
        if (list) {
            html.push(`<${list.tag}>${list.items.map((i) => `<li><p>${inline(i)}</p></li>`).join("")}</${list.tag}>`);
            list = null;
        }
    };
    const flushQuote = () => {
        if (quote.length) {
            html.push(`<blockquote><p>${inline(quote.join(" "))}</p></blockquote>`);
            quote = [];
        }
    };
    const flushAll = () => {
        flushParagraph();
        flushList();
        flushQuote();
    };

    for (const raw of lines) {
        if (fence) {
            if (/^```/.test(raw)) {
                html.push(`<pre><code>${escapeHtml(fence.join("\n"))}</code></pre>`);
                fence = null;
            } else {
                fence.push(raw);
            }
            continue;
        }
        const line = raw.replace(/\s+$/, "");
        if (/^```/.test(line)) {
            flushAll();
            fence = [];
            continue;
        }
        if (line.trim() === "") {
            flushAll();
            continue;
        }
        const heading = /^(#{1,6})\s+(.*)$/.exec(line);
        if (heading) {
            flushAll();
            html.push(`<h${heading[1].length}>${inline(heading[2].replace(/\s+#+$/, ""))}</h${heading[1].length}>`);
            continue;
        }
        if (/^(-{3,}|\*{3,}|_{3,})$/.test(line.trim())) {
            flushAll();
            html.push("<hr>");
            continue;
        }
        const bullet = /^\s*[-*+]\s+(.*)$/.exec(line);
        const numbered = /^\s*\d+\.\s+(.*)$/.exec(line);
        if (bullet || numbered) {
            flushParagraph();
            flushQuote();
            const tag = bullet ? "ul" : "ol";
            if (!list || list.tag !== tag) {
                flushList();
                list = { tag, items: [] };
            }
            list.items.push((bullet ?? numbered)![1]);
            continue;
        }
        const quoted = /^>\s?(.*)$/.exec(line);
        if (quoted) {
            flushParagraph();
            flushList();
            quote.push(quoted[1]);
            continue;
        }
        if (list && /^\s{2,}\S/.test(line)) {
            list.items[list.items.length - 1] += " " + line.trim();
            continue;
        }
        flushList();
        flushQuote();
        paragraph.push(line.trim());
    }
    if (fence) {
        html.push(`<pre><code>${escapeHtml(fence.join("\n"))}</code></pre>`);
    }
    flushAll();
    return html.join("\n");
}
